import { QueryParameterBag } from '@smithy/types'

import type { LoggerLike as Logger } from '@droidmap/shared-telemetry'

export const DEFAULT_PAGE_SIZE = 50

/**
 * Largest page the offset-paginated list endpoints accept — their route
 * schemas cap `pageSize` at 100. {@link processAllOffsetPages} walks with this
 * by default so "fetch every page" does the fewest round trips.
 */
export const MAX_PAGE_SIZE = 100

/**
 * Largest page number the offset-paginated list endpoints accept. The offset a
 * page implies is `page * pageSize`, and it has to stay a number Postgres will
 * take, so the routes refuse anything past this rather than letting the
 * database answer with an error.
 */
export const MAX_PAGE = 100_000

/**
 * Pagination cursor type - an opaque string token representing the position in a paginated result set.
 * This is a base64-encoded representation of the DynamoDB LastEvaluatedKey.
 * Clients should treat this as an opaque value and not attempt to parse or modify it.
 */
export type PaginationCursor = string

/**
 * Encode a DynamoDB LastEvaluatedKey object into an opaque cursor string.
 * Uses URL-safe base64 encoding for safe use in query parameters.
 * Accepts object type from OneTable's next cursor.
 */
export const encodeCursor = (cursorObject: object): string => {
  return Buffer.from(JSON.stringify(cursorObject)).toString('base64url')
}

/**
 * Decode an opaque cursor string back into a DynamoDB LastEvaluatedKey object.
 * @throws {Error} If the cursor is invalid or doesn't decode to an object
 */
export const decodeCursor = (cursor: string): Record<string, unknown> => {
  const decoded = JSON.parse(Buffer.from(cursor, 'base64url').toString('utf-8'))

  if (
    typeof decoded !== 'object' ||
    decoded === null ||
    Array.isArray(decoded)
  ) {
    throw new Error('Invalid pagination cursor: expected object')
  }

  return decoded as Record<string, unknown>
}

/**
 * Parameters for formatting a paginated response.
 * Accepts raw DynamoDB cursor objects (typed as object from OneTable) which will be encoded to opaque strings.
 */
export type FormatPaginatedResponseParams<T> = {
  items: T[]
  next?: object
  prev?: object | PaginationCursor
}

/**
 * Format a paginated response by encoding cursor objects into opaque strings.
 * This helper centralizes cursor encoding logic for consistent pagination behavior.
 *
 * @param params - Parameters object containing:
 *   - items: Array of items for this page
 *   - next: Optional DynamoDB LastEvaluatedKey object for next page (will be encoded to string)
 *   - prev: Optional cursor for previous page. Can be:
 *     - DynamoDB LastEvaluatedKey object (will be encoded to string)
 *     - Already encoded PaginationCursor string from client (passed through as-is)
 * @returns Paged response with encoded cursors
 *
 * @example
 * // Typical usage: pass DynamoDB next cursor and client's cursor as prev
 * return formatPaginatedResponse({
 *   items: projects,
 *   next: pagedProjects.next,  // DynamoDB LastEvaluatedKey object -> encoded to string
 *   prev: next                 // Client's cursor string -> passed through (becomes prev in response)
 * })
 *
 * @example
 * // If DynamoDB returns both cursors (less common)
 * return formatPaginatedResponse({
 *   items: projects,
 *   next: pagedProjects.next,  // DynamoDB object -> encoded
 *   prev: pagedProjects.prev   // DynamoDB object -> encoded
 * })
 */
export const formatPaginatedResponse = <T>(
  params: FormatPaginatedResponseParams<T>,
): Paged<T> => {
  const { items, next, prev } = params
  return {
    items,
    next: next ? encodeCursor(next) : undefined,
    prev: prev
      ? typeof prev === 'string'
        ? prev // Already encoded, pass through
        : encodeCursor(prev) // DynamoDB object, encode it
      : undefined,
  }
}

export type Paged<T> = {
  items: T[]
  next?: PaginationCursor
  prev?: PaginationCursor
}

export class PagedUtils {
  static empty = <T>(): Paged<T> => {
    return {
      items: [],
    }
  }
}

export type PaginationControls = {
  next?: PaginationCursor
  prev?: PaginationCursor
  pageSize?: number
}

export type SortDirection = 'asc' | 'desc'

/**
 * Offset-based list controls for PG-backed list endpoints. Cursor-based
 * {@link PaginationControls} stays for DDB-backed endpoints whose resume
 * token is an opaque LastEvaluatedKey.
 */
export type OffsetPaginationControls<TSortKey extends string = string> = {
  /** 0-indexed page number. Defaults to 0. */
  page?: number
  /** Items per page. Defaults to {@link DEFAULT_PAGE_SIZE}. */
  pageSize?: number
  /** Free-text substring; the DAO decides which columns to ILIKE-match. */
  filter?: string
  /** Per-endpoint sort column. Validated by the route's zod schema. */
  sortBy?: TSortKey
  /** Sort direction. Defaults to 'desc'. */
  sortDir?: SortDirection
}

export type OffsetPaged<T> = {
  items: T[]
  /** 0-indexed page number that was returned. */
  page: number
  pageSize: number
  /** Total rows matching the filter (across all pages), not just this page. */
  totalCount: number
}

export class OffsetPagedUtils {
  static empty = <T>(
    page = 0,
    pageSize = DEFAULT_PAGE_SIZE,
  ): OffsetPaged<T> => ({
    items: [],
    page,
    pageSize,
    totalCount: 0,
  })
}

/**
 * Page start marker for DynamoDB queries.
 * Contains the decoded cursor object for either forward (next) or backward (prev) pagination.
 */
export type PageStartMarker = {
  /** Decoded DynamoDB LastEvaluatedKey for forward pagination */
  next?: Record<string, unknown>
  /** Decoded DynamoDB LastEvaluatedKey for backward pagination */
  prev?: Record<string, unknown>
}

/**
 * Calculate the page start marker for DynamoDB queries by decoding pagination cursors.
 * This function converts opaque cursor strings into DynamoDB-compatible LastEvaluatedKey objects.
 *
 * @param next - Optional opaque cursor string for forward pagination (fetching next page)
 * @param prev - Optional opaque cursor string for backward pagination (fetching previous page)
 * @returns PageStartMarker object containing decoded cursor for use in DynamoDB queries
 * @throws {Error} If both next and prev cursors are provided simultaneously
 * @throws {Error} If cursor string is invalid or doesn't decode to an object
 */
export const calculatePageStartMarker = (
  next?: PaginationCursor,
  prev?: PaginationCursor,
): PageStartMarker => {
  let pageStartMarker: PageStartMarker = {}
  if (next && prev) {
    throw new Error(
      'Cannot specify both next and prev when requesting paged results',
    )
  } else if (next) {
    // Decode the opaque cursor string back to DynamoDB object
    pageStartMarker = {
      next: decodeCursor(next),
    }
  } else if (prev) {
    // Decode the opaque cursor string back to DynamoDB object
    pageStartMarker = {
      prev: decodeCursor(prev),
    }
  }

  return pageStartMarker
}

export const paginationControlsFromQueryParams = (
  params?: {
    [name: string]: string | undefined
  },
  log?: Logger,
): PaginationControls => {
  log?.debug('Parsing pagination controls from query params', { params })

  let next: PaginationCursor | undefined
  let prev: PaginationCursor | undefined
  let pageSize: number | undefined

  if (params) {
    const {
      next: nextString,
      prev: prevString,
      pageSize: pageSizeString,
    } = params

    // Cursors are already opaque strings, no need to parse
    next = nextString
    prev = prevString
    pageSize = pageSizeString ? parseInt(pageSizeString) : undefined
  }

  log?.debug('Parsed pagination controls', { next, prev, pageSize })

  return {
    next,
    prev,
    pageSize,
  }
}

export const queryParameterBagFromPaginationControls = (
  controls: PaginationControls,
): QueryParameterBag => {
  const { next, prev, pageSize } = controls

  const bag: {
    [x: string]: string | string[] | undefined
  } = {
    // Cursors are already opaque strings, pass through directly
    next,
    prev,
    pageSize: pageSize ? pageSize.toString() : undefined,
  }

  // Remove any undefined values
  Object.keys(bag).forEach((key) => bag[key] === undefined && delete bag[key])

  return bag as QueryParameterBag
}

export const offsetPaginationControlsFromQueryParams = <
  TSortKey extends string = string,
>(
  params?: { [name: string]: string | undefined },
  log?: Logger,
): OffsetPaginationControls<TSortKey> => {
  log?.debug('Parsing offset pagination controls from query params', { params })

  if (!params) return {}

  const {
    page: pageString,
    pageSize: pageSizeString,
    filter,
    sortBy,
    sortDir,
  } = params

  const controls: OffsetPaginationControls<TSortKey> = {}
  if (pageString !== undefined) controls.page = parseInt(pageString)
  if (pageSizeString !== undefined) controls.pageSize = parseInt(pageSizeString)
  if (filter) controls.filter = filter
  if (sortBy) controls.sortBy = sortBy as TSortKey
  if (sortDir === 'asc' || sortDir === 'desc') controls.sortDir = sortDir

  log?.debug('Parsed offset pagination controls', { controls })
  return controls
}

export const queryParameterBagFromOffsetPaginationControls = <
  TSortKey extends string = string,
>(
  controls: OffsetPaginationControls<TSortKey>,
): QueryParameterBag => {
  const { page, pageSize, filter, sortBy, sortDir } = controls

  const bag: { [x: string]: string | string[] | undefined } = {
    page: page !== undefined ? page.toString() : undefined,
    pageSize: pageSize !== undefined ? pageSize.toString() : undefined,
    filter: filter || undefined,
    sortBy: sortBy || undefined,
    sortDir: sortDir || undefined,
  }

  Object.keys(bag).forEach((key) => bag[key] === undefined && delete bag[key])

  return bag as QueryParameterBag
}

/**
 * Ensures a pagination cursor is properly encoded as a string.
 * If the cursor is already a string, returns it as-is.
 * If the cursor is an object, encodes it to a string.
 *
 * This provides backward compatibility for APIs that may return object cursors.
 *
 * @param cursor - The cursor to normalize (string or object)
 * @returns A properly a stringified JSON cursor
 */
export const normalizeCursor = (
  cursor: PaginationCursor | object,
): PaginationCursor => {
  if (typeof cursor === 'string') {
    return cursor
  }

  return JSON.stringify(cursor)
}

/**
 * Generic utility for processing all pages of a paginated result.
 * Handles the while loop pattern internally to iterate through all pages.
 *
 * @param fetcher - Function that fetches a page of results given pagination controls
 * @param processor - Optional function called for each page of items
 * @returns Promise resolving to all items from all pages
 *
 * @example
 * // Collect all items
 * const allAssetSets = await processAllPages(
 *   (controls) => assetServiceClient.listAssetSetsByProject({ projectId, ...controls }, metadata)
 * );
 *
 * @example
 * // Process items on each page
 * await processAllPages(
 *   (controls) => taskDomain.listTasksByProject({ projectId }, controls),
 *   async (tasks) => {
 *     await Promise.all(tasks.map(task => deleteTask(task.id)));
 *   }
 * );
 */
export async function processAllPages<T>(
  fetcher: (controls: { next?: PaginationCursor }) => Promise<Paged<T>>,
  processor?: (items: T[]) => Promise<void> | void,
): Promise<T[]> {
  const allItems: T[] = []
  let next: PaginationCursor | undefined

  do {
    const page = await fetcher({ next })

    if (page.next) {
      page.next = normalizeCursor(page.next)
    }

    if (page.items.length > 0) {
      allItems.push(...page.items)

      if (processor) {
        await processor(page.items)
      }
    }

    next = page.next
  } while (next)

  return allItems
}

/**
 * Generic utility for processing all pages of an OFFSET-paginated result.
 *
 * The cursor-based {@link processAllPages} cannot drive offset endpoints:
 * {@link OffsetPaged} carries no `next` token, so its `while (next)` loop
 * runs exactly once and silently returns only the first page. Use this for
 * any endpoint returning {@link OffsetPaged} (PG-backed list routes such as
 * `listAssetSetsByProject` / `listTasksByProject`).
 *
 * Walks pages by incrementing `page` until `totalCount` rows have been
 * collected. A short page (fewer than `pageSize` items) also terminates the
 * walk, guarding against an infinite loop if `totalCount` and the returned
 * rows ever disagree.
 *
 * @param fetcher - Fetches one page given `{ page, pageSize }`.
 * @param options.pageSize - Rows per request. Defaults to {@link MAX_PAGE_SIZE};
 *   pass a smaller value only for an endpoint whose cap is below it.
 * @param options.processor - Optional callback invoked with each page's items.
 * @returns Promise resolving to all items across all pages.
 *
 * @example
 * const allAssetSets = await processAllOffsetPages((controls) =>
 *   assetServiceClient.listAssetSetsByProject({ projectId, ...controls }, metadata),
 * )
 */
export async function processAllOffsetPages<T>(
  fetcher: (controls: {
    page: number
    pageSize: number
  }) => Promise<OffsetPaged<T>>,
  options: {
    pageSize?: number
    processor?: (items: T[]) => Promise<void> | void
  } = {},
): Promise<T[]> {
  const pageSize = options.pageSize ?? MAX_PAGE_SIZE
  // A non-positive pageSize is unrecoverable: a 0-item page never satisfies
  // the `items.length < pageSize` short-page break, so the walk would spin
  // forever while `totalCount` rows remain. Fail fast rather than hang.
  if (!Number.isInteger(pageSize) || pageSize < 1) {
    throw new Error(
      `processAllOffsetPages: pageSize must be a positive integer, got ${pageSize}`,
    )
  }
  const allItems: T[] = []
  let page = 0
  let totalCount = Infinity

  while (allItems.length < totalCount) {
    const result = await fetcher({ page, pageSize })
    totalCount = result.totalCount

    if (result.items.length > 0) {
      allItems.push(...result.items)

      if (options.processor) {
        await options.processor(result.items)
      }
    }

    // A short page means there are no further rows — stop here so a stale
    // or inconsistent `totalCount` can't spin this into an infinite loop.
    if (result.items.length < pageSize) break
    page += 1
  }

  return allItems
}
